import { useEffect } from "react";
import { useNavigate, useParams, useSearchParams } from "react-router-dom";
import { toast } from "sonner";
import { claimCreatorReferral, finishOAuth, getOnboardingProfile, setAccessToken } from "@/lib/api";
import { saveProfile, setUser } from "@/lib/storage";
import { takeReturn } from "@/lib/returnTo";

// The Android app starts OAuth with its own deep link, which the API signs into `state`.
// When it is present, the code belongs to the app: hand it over instead of signing in here.
function appRedirectFromState(state) {
  try {
    const payload = JSON.parse(atob(state.split(".")[1].replace(/-/g, "+").replace(/_/g, "/")));
    const target = payload.redirect_uri;
    return typeof target === "string" && /^hustlrmate:\/\/oauth\/(google|github)$/.test(target) ? target : null;
  } catch {
    return null;
  }
}

export default function OAuthCallback() {
  const { provider } = useParams();
  const [params] = useSearchParams();
  const navigate = useNavigate();
  const code = params.get("code");
  const state = params.get("state");
  const appRedirect = code && state ? appRedirectFromState(state) : null;
  const appUrl = appRedirect ? `${appRedirect}?code=${encodeURIComponent(code)}&state=${encodeURIComponent(state)}` : null;

  useEffect(() => {
    if (appUrl) {
      window.location.replace(appUrl);
      return;
    }
    const completeLogin = async () => {
      const code = params.get("code");
      const state = params.get("state");
      if (!code || !state || !provider) {
        toast("OAuth login failed.", { description: "The provider did not return a valid login response." });
        navigate("/auth", { replace: true });
        return;
      }

      try {
        const session = await finishOAuth(provider, { code, state });
        setAccessToken(session.accessToken);
        setUser(session.user);
        const referralCode = localStorage.getItem("hm_creator_referral_code");
        if (referralCode) await claimCreatorReferral(referralCode).catch(() => {});
        const profile = await getOnboardingProfile();
        saveProfile(profile);
        toast("Welcome to HustlrMate.", { description: "Your account is connected." });
        navigate((profile.completedAt || profile.isComplete) ? takeReturn("/dashboard") : "/onboarding", { replace: true });
      } catch (error) {
        toast("OAuth login failed.", { description: error.message });
        navigate("/auth", { replace: true });
      }
    };

    completeLogin();
  }, [navigate, params, provider, appUrl]);

  if (appUrl) {
    return (
      <div className="min-h-screen bg-[#0A0A0A] text-[#F5F5F0] flex items-center justify-center px-6">
        <div className="text-center">
          <h1 className="font-serif-editorial text-4xl tracking-tight">Returning to the app...</h1>
          <p className="mt-3 text-sm text-[#A1A1AA]">If HustlrMate doesn't open by itself, continue below.</p>
          <a href={appUrl} data-testid="oauth-open-app" className="hm-btn-primary mt-6 inline-flex px-6 py-3 rounded-md text-sm font-semibold">Open HustlrMate</a>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-[#0A0A0A] text-[#F5F5F0] flex items-center justify-center px-6">
      <div className="text-center">
        <div className="overline mb-3">HustlrMate</div>
        <h1 className="font-serif-editorial text-4xl tracking-tight">Finishing sign in...</h1>
      </div>
    </div>
  );
}
